import { FC, useCallback, useEffect } from "react";
import { Button, Stack } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InsertChartOutlinedOutlinedIcon from "@mui/icons-material/InsertChartOutlinedOutlined";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setCurrentBudgetData } from "@/store/budgets/budgetsSlice";
import { FilterTreeNodeWithId } from "@/components/pages/common/filters-tree/utils/types/common";
import { BudgetViewSelectionWrapper } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/budgets/components/create-budget/components/BudgetViewSelectionWrapper";
import { FormInitialValues } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/budgets/components/create-budget/utils/types/formInitialValues";
import { BudgetFilterTree } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/budgets/components/create-budget/components/filters/BudgetFilterTree";
import { getResourceExplorerDataByBudgetThunk } from "@/store/budgets/thunks/getResourceExplorerDataByBudgetThunk";
import { resourceExplorerViewsHierarchyDefaultViewSelector } from "@/store/resource-explorer/selectors/views-hierarchy/resourceExplorerViewsHierarchyDefaultViewSelector";

interface BudgetsFiltersTabsProps {
  onChange(id: string): void;
  selectedItemId?: string;
  basedOn: FormInitialValues["basedOn"];
  setFieldValue(key: string, val: FilterTreeNodeWithId | string): void;
  filterTree?: FilterTreeNodeWithId;
}

export const BudgetsFiltersTabs: FC<BudgetsFiltersTabsProps> = ({
  onChange,
  selectedItemId,
  basedOn,
  setFieldValue,
  filterTree,
}) => {
  const dispatch = useAppDispatch();
  const defaultBillingReportId = useAppSelector(
    resourceExplorerViewsHierarchyDefaultViewSelector,
  );

  const onViewTabSelect = useCallback(() => {
    setFieldValue("basedOn", "viewId");
    dispatch(setCurrentBudgetData({ basedOn: "viewId" }));
  }, [setFieldValue, dispatch]);

  const onFilterTreeTabSelect = useCallback(() => {
    setFieldValue("basedOn", "filterTree");
    dispatch(setCurrentBudgetData({ basedOn: "filterTree" }));
    if (!defaultBillingReportId) {
      return;
    }
    setFieldValue("viewId", defaultBillingReportId);
    dispatch(
      setCurrentBudgetData({
        viewId: defaultBillingReportId,
      }),
    );
  }, [setFieldValue, dispatch, defaultBillingReportId]);

  useEffect(() => {
    if (!defaultBillingReportId) {
      return;
    }

    dispatch(getResourceExplorerDataByBudgetThunk());
  }, [defaultBillingReportId, dispatch, setFieldValue, basedOn]);

  return (
    <Stack spacing={2} display="block">
      <Stack
        direction="row"
        bgcolor="grey.200"
        p={0.5}
        borderRadius={1}
        spacing={0.5}
      >
        <Button
          startIcon={<InsertChartOutlinedOutlinedIcon fontSize="small" />}
          sx={{
            color: basedOn === "viewId" ? "primary" : "text.secondary",
            flex: 1,
            bgcolor: basedOn === "viewId" ? "white" : "inherit",
            "&:hover": {
              bgcolor: basedOn === "viewId" ? "grey.100" : "inherit",
            },
          }}
          onClick={onViewTabSelect}
        >
          Billing Report
        </Button>
        <Button
          startIcon={<FilterAltOutlinedIcon fontSize="small" />}
          sx={{
            color: basedOn === "filterTree" ? "primary" : "text.secondary",
            flex: 1,
            bgcolor: basedOn === "filterTree" ? "white" : "inherit",
            "&:hover": {
              bgcolor: basedOn === "filterTree" ? "grey.100" : "inherit",
            },
          }}
          onClick={onFilterTreeTabSelect}
        >
          Custom Filters
        </Button>
      </Stack>
      {basedOn === "viewId" && (
        <BudgetViewSelectionWrapper
          selectedItemId={selectedItemId}
          onChange={onChange}
        />
      )}
      {basedOn === "filterTree" && (
        <BudgetFilterTree
          setFieldValue={setFieldValue}
          filterTree={filterTree}
        />
      )}
    </Stack>
  );
};
